import {useAufgabenHandling} from "../../hooks/aufgaben/useAufgabenHandling";
import {bindPopover, bindTrigger, usePopupState} from "material-ui-popup-state/hooks";
import * as Yup from "yup";
import {
    Box,
    Button,
    capitalize,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Typography,
} from "@mui/material";
import {Field, FieldArray, Form, Formik, FormikHelpers} from "formik";
import TelefonnummerInput from "../TelefonnummerInput";
import {countries} from "../data/Flags/CountryInfo";
import {useUser} from "../../user";
import {useMemo} from "react";
import {KontakteTag} from "../../api";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {TextField as FormikTextField} from "formik-mui";

interface ButtonShowTestCallDialogProps {
    aufgabeId: string;
    language?: string;
}

interface FormValues {
    telefonnummer: string;
    firstName: string;
    lastName: string;
    language: string;
    tags: Array<KontakteTag>;
}

export default function ButtonShowTestCallDialog({aufgabeId, language}: ButtonShowTestCallDialogProps) {
    const {translation, starteTestCallMutation} = useAufgabenHandling();
    const me = useUser();
    const popupState = usePopupState({
        variant: 'popover',
        popupId: `show-test-call-dialog`,
    });

    const validationSchema = Yup.object({
        telefonnummer: Yup.string()
            .required(capitalize(translation("task-test-attributes-phone-required-message")))
            .matches(/^[1-9][0-9]*$/, capitalize(translation("task-test-attributes-phone-validation-failed-message"))),
        firstName: Yup.string(),
        lastName: Yup.string(),
        language: Yup.string(),
        tags: Yup.array().of(
            Yup.object().shape({
                tag: Yup.string().required(capitalize(translation("task-test-attributes-tag-name-required-message"))),
                replacementString: Yup.string().required(capitalize(translation("task-test-attributes-tag-replacement-required-message"))),
            })
        ),
    });

    const initialValues: FormValues = useMemo(() => ({
        telefonnummer: "",
        firstName: me.vorname || "",
        lastName: me.nachname || "",
        language: language || "",
        tags: [],
    }), [me.vorname, me.nachname, language]);

    const onSubmit = (
        values: FormValues,
        formikHelpers: FormikHelpers<FormValues>
    ) => {
        starteTestCallMutation.mutate(
            {
                telefonnummer: countries.de.prefix + values.telefonnummer,
                aufgabeId,
                firstName: values.firstName || null,
                lastName: values.lastName || null,
                language: values.language || null,
                tags: values.tags.length > 0 ? values.tags : null,
            },
            {
                onSettled: () => {
                    popupState.close();
                    formikHelpers.resetForm();
                },
            }
        );
    };

    return (
        <>
            <Button
                id={`button-show-test-call`}
                color={"primary"}
                variant={"contained"}
                aria-label={`button-show-test-call`}
                {...bindTrigger(popupState)}
            >
                {capitalize(translation("task-test-call"))}
            </Button>

            <Dialog id={`dialog-show-test-call`} {...bindPopover(popupState)}>
                <DialogTitle id={`dialog-title-show-test-call`}>
                    {capitalize(translation("task-test"))}
                </DialogTitle>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                    enableReinitialize
                >
                    {({ isValid, dirty, values }) => (
                        <Form>
                            <DialogContent id={`dialog-content-show-test-call`}>
                                <DialogContentText id={`dialog-content-text-show-test-call`}>
                                    {capitalize(translation("task-test-telefonnummer-description"))}
                                </DialogContentText>

                                <Box mt={2}>
                                    <Field
                                        name="telefonnummer"
                                        component={TelefonnummerInput}
                                        label={capitalize(translation("task-test-telefonnummer-title"))}
                                        required
                                        country={countries.de}
                                    />
                                </Box>

                                <Box mt={2} display="flex" gap={2}>
                                    <Field
                                        component={FormikTextField}
                                        name="firstName"
                                        label={capitalize(translation("task-test-attributes-first-name"))}
                                        fullWidth
                                    />
                                    <Field
                                        component={FormikTextField}
                                        name="lastName"
                                        label={capitalize(translation("task-test-attributes-last-name"))}
                                        fullWidth
                                    />
                                </Box>

                                <Box mt={3}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        {capitalize(translation("task-test-attributes-tags-optional"))}
                                    </Typography>
                                    <FieldArray name="tags">
                                        {({ push, remove }) => (
                                            <Box>
                                                {values.tags.map((tag, index) => (
                                                    <Box key={index} display="flex" gap={2} mt={2} alignItems="flex-start">
                                                        <Field
                                                            component={FormikTextField}
                                                            name={`tags.${index}.tag`}
                                                            label={capitalize(translation("task-test-attributes-tag-name"))}
                                                            required
                                                            fullWidth
                                                        />
                                                        <Field
                                                            component={FormikTextField}
                                                            name={`tags.${index}.replacementString`}
                                                            label={capitalize(translation("task-test-attributes-tag-replacement"))}
                                                            required
                                                            fullWidth
                                                        />
                                                        <IconButton
                                                            onClick={() => remove(index)}
                                                            color="error"
                                                            aria-label={capitalize(translation("task-test-attributes-tag-remove"))}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                                <Box mt={2}>
                                                    <Button
                                                        startIcon={<AddIcon />}
                                                        onClick={() => push({ tag: "", replacementString: "" })}
                                                        variant="outlined"
                                                        size="small"
                                                    >
                                                        {capitalize(translation("task-test-attributes-tag-add"))}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}
                                    </FieldArray>
                                </Box>
                            </DialogContent>

                            <DialogActions>
                                <Button
                                    id={`dialog-action-cancel-show-test-call`}
                                    color={"inherit"}
                                    onClick={() => {
                                        popupState.close();
                                    }}
                                >
                                    {capitalize(translation("abort"))}
                                </Button>

                                <Button
                                    id={`dialog-action-confirm-show-test-call`}
                                    type="submit"
                                    color={"primary"}
                                    variant={"contained"}
                                    disabled={!dirty || !isValid}
                                >
                                    {capitalize(translation("test"))}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </>
    );
}